import type { FullPostType, SharingModalFactoryProps } from '../../factories/types';
import type { FacebookPost, RedditPost } from '../../types';

import type { ActionsReturnType, GroupSharingHookProps, PostSharingHookProps, StorySharingHookProps } from './types';
import { useFacebookGroupPostSharing } from './useFacebookGroupPostSharing';
import { useFacebookPostSharing } from './useFacebookPostSharing';
import { useFacebookStorySharing } from './useFacebookStorySharing';
import { useInstagramPostSharing } from './useInstagramPostSharing';
import { useInstagramStorySharing } from './useInstagramStorySharing';
import { useRedditGroupPostSharing } from './useRedditGroupPostSharing';

type SharingHook<P> = (props: P) => ActionsReturnType;
type Executor = (props: SharingModalFactoryProps) => ActionsReturnType;

const createEntry = <P>(hook: SharingHook<P>, getProps: (props: SharingModalFactoryProps) => P): Executor => {
	return (props) => {
		const prepared = getProps(props);
		return hook(prepared);
	};
};

// Add more executors as more post types are supported
export const EXECUTORS: Readonly<Record<FullPostType, Executor>> = {
	facebook_post: createEntry(useFacebookPostSharing, (props): PostSharingHookProps => {
		if ('message' in props.sharingResource) {
			return { message: props.sharingResource.message };
		}
		throw new Error('Unexpected sharing resource for post type', {
			cause: { fullPostType: 'facebook_post', sharingResource: props.sharingResource },
		});
	}),
	facebook_group_post: createEntry(useFacebookGroupPostSharing, (props): GroupSharingHookProps => {
		if ('message' in props.sharingResource) {
			return { recommendedGroup: (props as FacebookPost).recommendedGroup, message: props.sharingResource.message };
		}
		throw new Error('Unexpected sharing resource for group post type', {
			cause: { fullPostType: 'facebook_group_post', sharingResource: props.sharingResource },
		});
	}),
	facebook_story: createEntry(useFacebookStorySharing, (props): StorySharingHookProps => {
		if ('shareUrl' in props.sharingResource) {
			return { shareUrl: props.sharingResource.shareUrl };
		}
		throw new Error('Unexpected sharing resource for story type', {
			cause: { fullPostType: 'facebook_story', resource: props.sharingResource },
		});
	}),
	instagram_post: createEntry(useInstagramPostSharing, (props): PostSharingHookProps => {
		if ('message' in props.sharingResource) {
			return { message: props.sharingResource.message };
		}
		throw new Error('Unexpected sharing resource for post type', {
			cause: { fullPostType: 'instagram_post', sharingResource: props.sharingResource },
		});
	}),
	instagram_story: createEntry(useInstagramStorySharing, (props): StorySharingHookProps => {
		if ('shareUrl' in props.sharingResource) {
			return { shareUrl: props.sharingResource.shareUrl };
		}
		throw new Error('Unexpected sharing resource for story type', {
			cause: { fullPostType: 'instagram_story', resource: props.sharingResource },
		});
	}),
	reddit_post: createEntry(useRedditGroupPostSharing, (props): GroupSharingHookProps => {
		if ('message' in props.sharingResource) {
			return { recommendedGroup: (props as RedditPost).recommendedGroup, message: props.sharingResource.message };
		}
		throw new Error('Unexpected sharing resource for type', {
			cause: { fullPostType: 'reddit_post', resource: props.sharingResource },
		});
	}),
	reddit_group_post: createEntry(useRedditGroupPostSharing, (props): GroupSharingHookProps => {
		if ('message' in props.sharingResource) {
			return { recommendedGroup: (props as RedditPost).recommendedGroup, message: props.sharingResource.message };
		}
		throw new Error('Unexpected sharing resource for type', {
			cause: { fullPostType: 'reddit_group_post', resource: props.sharingResource },
		});
	}),
};
